// Site footer. The block at its top (call-to-action cards) varies by page; see FooterCtas.tsx.
// Converted from the Webflow footer symbols.

import type { ReactNode } from 'react';
import { SlideIn } from '@components/interactions';
import { NavLink } from '@components/webflow';

declare global {
  interface Window {
    // Cookiebot, the cookie-consent banner, which Google Tag Manager loads (see index.html).
    Cookiebot?: { renew: () => void };
  }
}

/** Reopens the Cookiebot banner so visitors can change their cookie choices. */
function CookieSettingsLink({ className, children }: { className: string; children: ReactNode }) {
  return (
    <a
      href="#"
      className={className}
      onClick={(e) => {
        e.preventDefault();
        window.Cookiebot?.renew();
      }}
    >
      {children}
    </a>
  );
}

export function SiteFooter({ cta }: { cta: ReactNode }) {
  return (
    <div>
      <footer className="footer5_component">
        <SlideIn className="padding-global" as="div">
          <div className="container-large">
            <div className="padding-vertical padding-section-large">
              {cta}
              <div className="padding-bottom padding-xxlarge">
                <div className="w-layout-grid footer5_top-wrapper">
                  <div className="footer5_link-column">
                    <div className="margin-bottom margin-xsmall">
                      <div className="text-weight-semibold">Products</div>
                    </div>
                    <div className="footer5_link-list">
                      <NavLink href="/dbos-transact" className="footer5_link">
                        {'DBOS\u00a0Transact'}
                      </NavLink>
                      <NavLink href="/dbos-conductor" className="footer5_link">
                        DBOS Conductor
                      </NavLink>
                      <NavLink href="/dbos-pricing" className="footer5_link">
                        Pricing Plans
                      </NavLink>
                      <NavLink href="/compare/dbos-vs-temporal" className="footer5_link">
                        DBOS vs. Temporal
                      </NavLink>
                    </div>
                  </div>
                  <div className="footer5_link-column">
                    <div className="margin-bottom margin-xsmall">
                      <div className="text-weight-semibold">Customer Stories</div>
                    </div>
                    <div className="footer5_link-list">
                      <a href="/case-studies/bms" className="footer5_link">
                        Bristol Myers Squibb
                      </a>
                      <a href="/case-studies/yutori-large-scale-durable-agentic-ai" className="footer5_link">
                        Yutori
                      </a>
                      <a href="/case-studies/dosu" className="footer5_link">
                        Dosu
                      </a>
                      <NavLink href="/customer-stories" className="footer5_link">
                        All stories
                      </NavLink>
                    </div>
                  </div>
                  <div className="footer5_link-column">
                    <div className="margin-bottom margin-xsmall">
                      <div className="text-weight-semibold">Developers</div>
                    </div>
                    <div className="footer5_link-list">
                      <a href="https://docs.dbos.dev/" className="footer5_link">
                        Docs
                      </a>
                      <a href="https://docs.dbos.dev/quickstart" className="footer5_link">
                        Quickstart Guide
                      </a>
                      <a href="https://docs.dbos.dev/python/examples/widget-store" className="footer5_link">
                        Examples
                      </a>
                      <a href="https://docs.dbos.dev/python/tutorials/workflow-tutorial" className="footer5_link">
                        Tutorials
                      </a>
                      <a href="/llms.txt" className="footer5_link">
                        LLMs.txt
                      </a>
                    </div>
                  </div>
                  <div className="footer5_link-column">
                    <div className="margin-bottom margin-xsmall">
                      <div className="text-weight-semibold">Company</div>
                    </div>
                    <div className="footer5_link-list">
                      <NavLink href="/contact" className="footer5_link">
                        Contact Sales
                      </NavLink>
                      <NavLink href="/blog" className="footer5_link">
                        Blog
                      </NavLink>
                      <NavLink href="/about" className="footer5_link">
                        About Us
                      </NavLink>
                      <a href="/about#team" target="_blank" className="footer5_link">
                        Our Team
                      </a>
                      <NavLink href="/partners" className="footer5_link">
                        Partners
                      </NavLink>
                      <NavLink href="/jobs" className="footer5_link">
                        Careers
                      </NavLink>
                    </div>
                  </div>
                </div>
              </div>
              <div className="margin-vertical margin-large">
                <div className="divider-horizontal" />
              </div>
              <div className="padding-top padding-medium">
                <div className="footer5_bottom-wrapper">
                  <NavLink
                    href="/"
                    id="w-node-_10146262-347e-bc86-a6c8-5cc4f0f78697-351fb178"
                    className="footer5_logo-link w-nav-brand"
                  >
                    <img
                      width="108"
                      src="/assets/images/67241465129b580e4b924af4_Vectors-Wrapper.svg"
                      alt="DBOS - Logo"
                      className="navigation-logo-new"
                    />
                  </NavLink>
                  <div className="w-layout-grid footer5_legal-list">
                    <div id="w-node-_10146262-347e-bc86-a6c8-5cc4f0f7869a-351fb178" className="footer5_credit-text">
                      {'© 2026 DBOS, Inc. '}
                    </div>
                    <NavLink href="/privacy" className="footer5_legal-link">
                      Privacy Policy
                    </NavLink>
                    <NavLink href="/terms-of-service" className="footer5_legal-link">
                      Terms of Service
                    </NavLink>
                    <NavLink href="/opn-pledge" className="footer5_legal-link">
                      Open Patent Pledge
                    </NavLink>
                    <CookieSettingsLink className="footer5_legal-link">Cookies Settings</CookieSettingsLink>
                  </div>
                  <div className="w-layout-grid footer5_social-icons">
                    <a href="https://discord.com/invite/jsmC6pXGgX" className="footer5_social-link w-inline-block">
                      <div className="icon-embed-xsmall w-embed">
                        <svg
                          width="100%"
                          height="100%"
                          viewBox="0 0 24 25"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          {'\n'}
                          <path
                            d="M14.2524 6.66699C14.096 6.94165 13.9556 7.22579 13.828 7.51624C12.6154 7.33629 11.3806 7.33629 10.1649 7.51624C10.0404 7.22579 9.89682 6.94165 9.74048 6.66699C8.60133 6.85958 7.49091 7.19738 6.43793 7.67409C4.3511 10.7333 3.78631 13.7136 4.06711 16.6528C5.28921 17.5462 6.6581 18.2282 8.11632 18.6638C8.44498 18.2282 8.73535 17.7641 8.98424 17.281C8.51199 17.1074 8.05569 16.8896 7.61855 16.637C7.73341 16.5549 7.8451 16.4697 7.95358 16.3876C10.5159 17.581 13.4834 17.581 16.0488 16.3876C16.1573 16.476 16.269 16.5612 16.3839 16.637C15.9467 16.8927 15.4904 17.1074 15.015 17.2842C15.2639 17.7672 15.5542 18.2313 15.8829 18.667C17.3411 18.2313 18.71 17.5525 19.9322 16.6591C20.2639 13.2495 19.3641 10.2945 17.5548 7.67725C16.5051 7.20054 15.3947 6.86273 14.2555 6.6733L14.2524 6.66699ZM9.34161 14.8438C8.55348 14.8438 7.89935 14.1366 7.89935 13.2621C7.89935 12.3876 8.52794 11.6773 9.33843 11.6773C10.1489 11.6773 10.7935 12.3908 10.7807 13.2621C10.7679 14.1335 10.1457 14.8438 9.34161 14.8438ZM14.6576 14.8438C13.8663 14.8438 13.2185 14.1366 13.2185 13.2621C13.2185 12.3876 13.8471 11.6773 14.6576 11.6773C15.4681 11.6773 16.1094 12.3908 16.0967 13.2621C16.0839 14.1335 15.4617 14.8438 14.6576 14.8438Z"
                            fill="currentColor"
                          />
                          {'\n'}
                        </svg>
                      </div>
                    </a>
                    <a href="https://github.com/dbos-inc" className="footer5_social-link w-inline-block">
                      <div className="icon-embed-xsmall w-embed">
                        <svg
                          width="100%"
                          height="100%"
                          viewBox="0 0 24 25"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          {'\n'}
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M12.0074 4.66699C7.5968 4.66699 4.03125 8.33366 4.03125 12.8698C4.03125 16.4958 6.31582 19.5652 9.48513 20.6515C9.88137 20.7332 10.0265 20.475 10.0265 20.2578C10.0265 20.0677 10.0135 19.4158 10.0135 18.7367C7.79468 19.2257 7.33264 17.7588 7.33264 17.7588C6.97607 16.8082 6.44774 16.5638 6.44774 16.5638C5.72154 16.0613 6.50064 16.0613 6.50064 16.0613C7.30619 16.1157 7.72888 16.9033 7.72888 16.9033C8.44186 18.1527 9.59076 17.7997 10.053 17.5823C10.1189 17.0527 10.3304 16.686 10.5548 16.4823C8.78521 16.2922 6.92333 15.586 6.92333 12.4352C6.92333 11.5388 7.24007 10.8055 7.74194 10.2352C7.66276 10.0315 7.38537 9.18933 7.82129 8.06216C7.82129 8.06216 8.49476 7.84483 10.0133 8.90416C10.6634 8.7246 11.3339 8.63326 12.0074 8.63249C12.6809 8.63249 13.3674 8.72766 14.0014 8.90416C15.5201 7.84483 16.1935 8.06216 16.1935 8.06216C16.6295 9.18933 16.3519 10.0315 16.2727 10.2352C16.7878 10.8055 17.0915 11.5388 17.0915 12.4352C17.0915 15.586 15.2296 16.2785 13.4468 16.4823C13.7374 16.7403 13.9881 17.2292 13.9881 18.0033C13.9881 19.1033 13.9751 19.9862 13.9751 20.2577C13.9751 20.475 14.1204 20.7332 14.5165 20.6517C17.6858 19.565 19.9704 16.4958 19.9704 12.8698C19.9834 8.33366 16.4048 4.66699 12.0074 4.66699Z"
                            fill="currentColor"
                          />
                          {'\n'}
                        </svg>
                      </div>
                    </a>
                    <a href="https://twitter.com/DBOS_Inc" className="footer5_social-link w-inline-block">
                      <div className="icon-embed-xsmall w-embed">
                        <svg
                          width="100%"
                          height="100%"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          {'\n'}
                          <path
                            d="M17.1761 4H19.9362L13.9061 10.7774L21 20H15.4456L11.0951 14.4066L6.11723 20H3.35544L9.80517 12.7508L3 4H8.69545L12.6279 9.11262L17.1761 4ZM16.2073 18.3754H17.7368L7.86441 5.53928H6.2232L16.2073 18.3754Z"
                            fill="CurrentColor"
                          />
                          {'\n'}
                        </svg>
                      </div>
                    </a>
                    <a href="https://www.linkedin.com/company/dbos-inc/" className="footer5_social-link w-inline-block">
                      <div className="icon-embed-xsmall w-embed">
                        <svg
                          width="100%"
                          height="100%"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          {'\n'}
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M4.5 3C3.67157 3 3 3.67157 3 4.5V19.5C3 20.3284 3.67157 21 4.5 21H19.5C20.3284 21 21 20.3284 21 19.5V4.5C21 3.67157 20.3284 3 19.5 3H4.5ZM8.52076 7.00272C8.52639 7.95897 7.81061 8.54819 6.96123 8.54397C6.16107 8.53975 5.46357 7.90272 5.46779 7.00413C5.47201 6.15897 6.13998 5.47975 7.00764 5.49944C7.88795 5.51913 8.52639 6.1646 8.52076 7.00272ZM12.2797 9.76176H9.75971H9.7583V18.3216H12.4217V18.1219C12.4217 17.742 12.4214 17.362 12.4211 16.9819V16.9818V16.9816V16.9815V16.9812C12.4203 15.9674 12.4194 14.9532 12.4246 13.9397C12.426 13.6936 12.4372 13.4377 12.5005 13.2028C12.7381 12.3253 13.5271 11.7586 14.4074 11.8979C14.9727 11.9864 15.3467 12.3141 15.5042 12.8471C15.6013 13.1803 15.6449 13.5389 15.6491 13.8863C15.6605 14.9339 15.6589 15.9815 15.6573 17.0292V17.0294C15.6567 17.3992 15.6561 17.769 15.6561 18.1388V18.3202H18.328V18.1149C18.328 17.6629 18.3278 17.211 18.3275 16.7591V16.759V16.7588C18.327 15.6293 18.3264 14.5001 18.3294 13.3702C18.3308 12.8597 18.276 12.3563 18.1508 11.8627C17.9638 11.1286 17.5771 10.5211 16.9485 10.0824C16.5027 9.77019 16.0133 9.5691 15.4663 9.5466C15.404 9.54401 15.3412 9.54062 15.2781 9.53721L15.2781 9.53721L15.2781 9.53721C14.9984 9.52209 14.7141 9.50673 14.4467 9.56066C13.6817 9.71394 13.0096 10.0641 12.5019 10.6814C12.4429 10.7522 12.3852 10.8241 12.2991 10.9314L12.2991 10.9315L12.2797 10.9557V9.76176ZM5.68164 18.3244H8.33242V9.76733H5.68164V18.3244Z"
                            fill="CurrentColor"
                          />
                          {'\n'}
                        </svg>
                      </div>
                    </a>
                    <a href="https://www.youtube.com/@DBOS-Inc" className="footer5_social-link w-inline-block">
                      <div className="icon-embed-xsmall w-embed">
                        <svg
                          width="100%"
                          height="100%"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          {'\n'}
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M20.5686 4.77345C21.5163 5.02692 22.2555 5.76903 22.5118 6.71673C23.1821 9.42042 23.1385 14.5321 22.5259 17.278C22.2724 18.2257 21.5303 18.965 20.5826 19.2213C17.9071 19.8831 5.92356 19.8015 3.40294 19.2213C2.45524 18.9678 1.71595 18.2257 1.45966 17.278C0.827391 14.7011 0.871044 9.25144 1.44558 6.73081C1.69905 5.78311 2.44116 5.04382 3.38886 4.78753C6.96561 4.0412 19.2956 4.282 20.5686 4.77345ZM9.86682 8.70227L15.6122 11.9974L9.86682 15.2925V8.70227Z"
                            fill="CurrentColor"
                          />
                          {'\n'}
                        </svg>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SlideIn>
      </footer>
    </div>
  );
}
